import React, { useMemo, useEffect } from 'react';
import * as THREE from 'three';
import { FillPattern } from '../../types/floorplannerTypes';
import { createFillMaterial } from './PatternFillMaterial';

interface PatternFillMeshProps {
  fillColor: number | undefined;
  fillPattern: FillPattern | undefined;
  attach?: string;
  useWorldSpace?: boolean;
  useLocalPositionCoords?: boolean;
  opacity?: number;
  side?: THREE.Side;
  transparent?: boolean;
  depthWrite?: boolean;
  depthTest?: boolean;
}

/**
 * Declarative R3F component for pattern fill rendering.
 * Uses useMemo for material creation and handles disposal on unmount.
 * Attaches as material to parent mesh.
 */
const PatternFillMesh: React.FC<PatternFillMeshProps> = ({
  fillColor,
  fillPattern,
  attach = "material",
  useWorldSpace = false,
  useLocalPositionCoords = false,
  opacity = 1,
  side = THREE.DoubleSide,
  transparent = false,
  depthWrite = true,
  depthTest = true,
}) => {
  // Create material with useMemo for performance
  const material = useMemo(() => {
    return createFillMaterial(fillColor, fillPattern, {
      side,
      opacity,
      transparent,
      depthWrite,
      depthTest,
      useWorldSpace,
      useLocalPositionCoords,
    });
  }, [fillColor, fillPattern, side, opacity, transparent, depthWrite, depthTest, useWorldSpace, useLocalPositionCoords]);

  // Dispose material on unmount
  useEffect(() => {
    return () => {
      // Only dispose if it's a ShaderMaterial (pattern fill)
      // MeshBasicMaterial instances are created fresh each time and should be disposed
      if (material instanceof THREE.ShaderMaterial) {
        // ShaderMaterials are cached, so we don't dispose them here
        // They are disposed via disposeAllPatternFillMaterials()
      } else {
        material.dispose();
      }
    };
  }, [material]);

  useEffect(() => {
    material.needsUpdate = true;
  }, [material]);

  return <primitive key={material.uuid} object={material} attach={attach} />;
};

export default PatternFillMesh;
